import * as Yup from 'yup';
import { Formik } from 'formik';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { Button, CheckboxWithLabel, CodeInput, FormModal, ModalActions, ModalContent, ModalTitle, PhoneMask, SubmitButton, useAlert } from '@uplift/ui-v2';
import React, { useCallback, useMemo } from 'react';
import { Box, Typography, FormHelperText } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { isAxiosError } from 'axios';
import { authVerifyOTP } from '@uplift/api';
import { LocalStorageService } from '@uplift/services';
import { asyncWait } from '@uplift/utils';

type OTPForm = {
  code: string;
  snooze2fa: boolean;
};

export type VerifyOTPModalProps = {
  contact: string;
  email?: string;
  phone?: string;
  userId: string;
  requestId: string;
  onClose: () => void;
  onSendNewCode: () => Promise<void>;
  onSuccess: (redirectUrl: string, code: string) => void;
};

const validationSchema = Yup.object().shape({
  code: Yup.string().length(4).required(),
});

export const VerifyModal: React.VFC<VerifyOTPModalProps> = ({ contact, email, phone, userId, requestId, onClose, onSendNewCode, onSuccess }) => {
  const alert = useAlert();
  const { classes } = useStyles();
  const isMobile = useIsMobile('sm');
  const l10n = useL10n();
  const [ newCodeLoading, setNewCodeLoading ] = React.useState<boolean>(false);
  const [ verificationLoading, setVerificationLoading ] = React.useState<boolean>(false);

  const sentTo = useMemo(() => {
    if (email) {
      return (
        <Typography>
          {l10n.auth.mfa.emailedCode} <strong>{email}</strong>
        </Typography>
      );
    }

    if (phone) {
      return (
        <Typography>
          {l10n.auth.mfa.textedCode} <strong><PhoneMask phone={phone} /></strong>
        </Typography>
      );
    }

    return null;
  }, [ email, l10n.auth.mfa.emailedCode, l10n.auth.mfa.textedCode, phone ]);

  const handleSubmit = useCallback(async (values: OTPForm) => {
    setVerificationLoading(true);
    try {
      const response = await authVerifyOTP(
        userId,
        {
          otp: values.code,
          deviceToken: LocalStorageService.getItem('deviceToken'),
          snooze_2fa: values.snooze2fa,
          useAccountEmail: contact === 'email',
          contactId: contact === 'email' ? undefined : contact,
          requestId
        }
      );

      onSuccess(response.data.redirectUrl, response.data.code);
      setVerificationLoading(false);
    } catch (err) {
      if (isAxiosError(err)) {
        if (err.response.status === 400) {
          alert.error(err.response.data.error);
        }
      }
    }
  }, [ alert, contact, onSuccess, requestId, userId ]);

  const handleRequestNewCode = useCallback(async () => {
    setNewCodeLoading(true);
    await Promise.all([
      onSendNewCode(),
      asyncWait(500)
    ]);
    setNewCodeLoading(false);
  }, [ onSendNewCode ]);

  const initialValues: OTPForm = {
    code: '',
    snooze2fa: false
  };

  return (
    <Formik
      validateOnMount
      validationSchema={validationSchema}
      enableReinitialize
      initialErrors={{
        code: '',
      }}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <FormModal
          open
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <ModalTitle onClose={onClose}>
            {l10n.auth.mfa.verifyYourIdentity}
          </ModalTitle>
          <ModalContent>
            {sentTo}
            <Box mt={3}>
              <CodeInput
                name="code"
                codeLength={4}
                autoFocus
              />
            </Box>
            <Box mt={2}>
              <FormHelperText>
                {l10n.phoneValidationModal.yourCodeWillExpire}
              </FormHelperText>
            </Box>
            <Box mt={2}>
              <CheckboxWithLabel name="snooze2fa" color="secondary" label={l10n.auth.mfa.snooze2fa} />
            </Box>
            <Box mt={2} ml={-2}>
              {isMobile ? (
                <Button
                  onClick={handleRequestNewCode}
                  loading={formik.isSubmitting}
                  disabled={formik.isSubmitting}
                  color="secondary"
                  className={classes.button}
                >
                  {l10n.phoneValidationModal.sendNewCode}
                </Button>
              ) : null}
            </Box>
          </ModalContent>
          <ModalActions>
            <Box display="flex" justifyContent="space-between" width="100%">
              {isMobile ? null : (
                <Button
                  onClick={handleRequestNewCode}
                  loading={newCodeLoading}
                  disabled={formik.isSubmitting || newCodeLoading}
                  color="secondary"
                >
                  {l10n.phoneValidationModal.sendNewCode}
                </Button>
              )}
              <SubmitButton
                size="medium"
                variant="contained"
                color="secondary"
                fullWidth={isMobile}
                loading={formik.isSubmitting || verificationLoading}
              >
                {l10n.phoneValidationModal.verifyCode}
              </SubmitButton>
            </Box>
          </ModalActions>
        </FormModal>
      )}
    </Formik>
  );
};

const useStyles = makeStyles()({
  button: {
    textTransform: 'capitalize'
  }
});
